/**
 * Form Validation Utilities
 * Provides validation functions for form inputs
 *
 * NOTE: For Zod-based validation, see:
 * - /schemas/orderFormSchema.ts (widget-specific schemas)
 * - /schemas/order.schema.ts (main order schemas)
 *
 * These utilities are for custom validation logic that doesn't fit into Zod schemas.
 */

import type { PlanData } from '../types';

export const validation = {
  /**
   * Validate email format
   * For Zod schema version, use emailSchema from orderFormSchema.ts
   */
  email(value: string): string | null {
    if (!value) {
      return 'Email is required for account access and notifications';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return 'Please enter a valid email address (e.g., john@company.com)';
    }

    return null;
  },

  /**
   * Validate required field
   */
  required(value: string | number | null | undefined, fieldName: string = 'This field'): string | null {
    if (value === null || value === undefined || value === '') {
      return `${fieldName} is required`;
    }
    return null;
  },

  /**
   * Validate phone number
   * For Zod schema version, use phoneSchema from orderFormSchema.ts
   * For formatting hook, use usePhoneValidation
   */
  phone(value: string): string | null {
    if (!value) {
      return 'Phone number is required for account verification';
    }

    // Accept various phone formats: (555) 555-5555, 555-555-5555, 5555555555
    const phoneRegex = /^[\d\s\-()]+$/;
    if (!phoneRegex.test(value) || value.replace(/\D/g, '').length < 10) {
      return 'Please enter a 10-digit phone number (e.g., 555-123-4567)';
    }

    return null;
  },

  /**
   * Validate ZIP code
   * For Zod schema version, use zipCodeSchema from orderFormSchema.ts
   * For formatting hook, use useZipValidation
   */
  zipCode(value: string): string | null {
    if (!value) {
      return 'ZIP code is required';
    }

    const zipRegex = /^\d{5}(-\d{4})?$/;
    if (!zipRegex.test(value)) {
      return 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)';
    }

    return null;
  },

  /**
   * Validate US state code
   * For Zod schema version, use stateSchema from orderFormSchema.ts
   */
  stateCode(value: string): string | null {
    if (!value) {
      return 'State is required';
    }

    if (value.length !== 2) {
      return 'Please use 2-letter state code (e.g., CA)';
    }

    const validStates = [
      'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
      'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
      'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
      'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
      'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
    ];

    if (!validStates.includes(value.toUpperCase())) {
      return 'Please enter a valid US state code';
    }

    return null;
  },

  /**
   * Validate street address
   */
  streetAddress(value: string): string | null {
    if (!value) {
      return 'Street address is required';
    }

    if (value.trim().length < 3) {
      return 'Please enter a valid street address';
    }

    if (value.length > 200) {
      return 'Street address is too long (max 200 characters)';
    }

    return null;
  },

  /**
   * Validate city name
   */
  city(value: string): string | null {
    if (!value) {
      return 'City is required';
    }

    if (value.trim().length < 2) {
      return 'Please enter a valid city name';
    }

    if (!/^[a-zA-Z\s\-'.]+$/.test(value)) {
      return 'City name can only contain letters, spaces, hyphens, and apostrophes';
    }

    return null;
  },

  /**
   * Validate full name (first and last)
   */
  fullName(value: string): string | null {
    if (!value) {
      return 'Name is required';
    }

    const trimmed = value.trim();
    if (trimmed.length < 2) {
      return 'Please enter your full name';
    }

    const nameParts = trimmed.split(/\s+/);
    if (nameParts.length < 2) {
      return 'Please enter both first and last name';
    }

    return null;
  },

  /**
   * Validate positive number
   */
  positiveNumber(value: number, fieldName: string = 'This field'): string | null {
    if (value === null || value === undefined) {
      return `${fieldName} is required to continue`;
    }

    if (isNaN(value) || value <= 0) {
      return `${fieldName} must be greater than zero (e.g., 100, 1500.50)`;
    }

    return null;
  },

  /**
   * Validate date is not in the past
   */
  futureDate(value: string, fieldName: string = 'Date'): string | null {
    if (!value) {
      return `${fieldName} is required to schedule this action`;
    }

    const date = new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date < today) {
      return `${fieldName} must be today or a future date`;
    }

    return null;
  },

  /**
   * Validate end date is after start date
   */
  dateRange(startDate: string, endDate: string): string | null {
    if (!startDate || !endDate) {
      return null; // Let required validation handle this
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (end <= start) {
      return 'End date must be after the start date. Please select a later date.';
    }

    return null;
  },

  /**
   * Validate URL format
   */
  url(value: string): string | null {
    if (!value) {
      return null; // Optional field
    }

    try {
      new URL(value);
      return null;
    } catch {
      return 'Please enter a valid URL (e.g., https://example.com)';
    }
  },

  /**
   * Validate container size
   */
  containerSize(value: string): string | null {
    if (!value) {
      return 'Please select a container size';
    }

    const validSizes = ['10Y', '20Y', '30Y', '40Y'];
    if (!validSizes.includes(value)) {
      return 'Please select a valid container size';
    }

    return null;
  },

  /**
   * Validate material type
   */
  materialType(value: string): string | null {
    if (!value) {
      return 'Please select a material type';
    }

    if (value.trim().length < 1) {
      return 'Please select a valid material type';
    }

    return null;
  },

  /**
   * Validate delivery date (must be today or future)
   */
  deliveryDate(value: string): string | null {
    if (!value) {
      return 'Please select a delivery date';
    }

    const selectedDate = new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      return 'Delivery date must be today or in the future';
    }

    return null;
  },

  /**
   * Validate text length with custom message
   */
  maxLength(value: string, max: number, fieldName: string = 'This field'): string | null {
    if (value && value.length > max) {
      return `${fieldName} is too long (max ${max} characters)`;
    }
    return null;
  },

  /**
   * Validate complete plan data
   * Returns validation errors keyed by field name
   */
  validatePlanData(planData: PlanData): Record<string, string> {
    const errors: Record<string, string> = {};

    // Validate project info
    if (!planData.project_info.project_name?.trim()) {
      errors.project_name = 'Project name is required';
    }
    if (!planData.project_info.project_address?.trim()) {
      errors.project_address = 'Project address is required';
    }
    if (!planData.project_info.project_type) {
      errors.project_type = 'Project type is required';
    }
    if (!planData.project_info.estimated_start_date) {
      errors.estimated_start_date = 'Start date is required';
    }
    if (!planData.project_info.estimated_completion_date) {
      errors.estimated_completion_date = 'Completion date is required';
    }

    // Validate date range
    if (planData.project_info.estimated_start_date && planData.project_info.estimated_completion_date) {
      const dateError = validation.dateRange(
        planData.project_info.estimated_start_date,
        planData.project_info.estimated_completion_date
      );
      if (dateError) {
        errors.estimated_completion_date = dateError;
      }
    }

    // Validate applicant info
    if (!planData.applicant_info.contact_name?.trim()) {
      errors.contact_name = 'Contact name is required';
    }
    if (!planData.applicant_info.contact_email?.trim()) {
      errors.contact_email = 'Contact email is required';
    } else {
      const emailError = validation.email(planData.applicant_info.contact_email);
      if (emailError) {
        errors.contact_email = emailError;
      }
    }
    if (!planData.applicant_info.contact_phone?.trim()) {
      errors.contact_phone = 'Contact phone is required';
    } else {
      const phoneError = validation.phone(planData.applicant_info.contact_phone);
      if (phoneError) {
        errors.contact_phone = phoneError;
      }
    }

    return errors;
  },
};

// ============================================================================
// Standalone Utility Functions
// ============================================================================

/**
 * Format phone number to (555) 123-4567 format
 * For hook version with state management, use usePhoneValidation
 */
export function formatPhoneNumber(value: string): string {
  if (!value) return '';
  const digits = value.replace(/\D/g, '').slice(0, 10);

  if (digits.length <= 3) {
    return digits;
  } else if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  } else {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
}

/**
 * Format ZIP code to 12345 or 12345-6789 format
 * For hook version with state management, use useZipValidation
 */
export function formatZipCode(value: string): string {
  if (!value) return '';
  const digits = value.replace(/\D/g, '').slice(0, 9);

  if (digits.length <= 5) {
    return digits;
  } else {
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  }
}

/**
 * Extract digits from formatted string (phone or ZIP)
 */
export function extractDigits(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Validate multiple fields and return errors object
 * Useful for batch validation
 *
 * @example
 * const errors = validateFields({
 *   email: { value: 'test@example.com', validator: 'email' },
 *   phone: { value: '5551234567', validator: 'phone', label: 'Phone Number' }
 * });
 */
export function validateFields(
  fields: Record<string, { value: string | number; validator: string; label?: string }>
): Record<string, string> {
  const errors: Record<string, string> = {};

  Object.entries(fields).forEach(([key, { value, validator: validatorKey, label }]) => {
    const fieldName = label || key;

    // Type-safe validation lookup
    switch (validatorKey) {
      case 'email':
        if (typeof value === 'string') {
          const error = validation.email(value);
          if (error) errors[key] = error;
        }
        break;
      case 'phone':
        if (typeof value === 'string') {
          const error = validation.phone(value);
          if (error) errors[key] = error;
        }
        break;
      case 'zipCode':
        if (typeof value === 'string') {
          const error = validation.zipCode(value);
          if (error) errors[key] = error;
        }
        break;
      case 'required': {
        const error = validation.required(value, fieldName);
        if (error) errors[key] = error;
        break;
      }
      case 'fullName':
        if (typeof value === 'string') {
          const error = validation.fullName(value);
          if (error) errors[key] = error;
        }
        break;
      case 'streetAddress':
        if (typeof value === 'string') {
          const error = validation.streetAddress(value);
          if (error) errors[key] = error;
        }
        break;
      case 'city':
        if (typeof value === 'string') {
          const error = validation.city(value);
          if (error) errors[key] = error;
        }
        break;
      case 'stateCode':
        if (typeof value === 'string') {
          const error = validation.stateCode(value);
          if (error) errors[key] = error;
        }
        break;
      case 'containerSize':
        if (typeof value === 'string') {
          const error = validation.containerSize(value);
          if (error) errors[key] = error;
        }
        break;
      case 'materialType':
        if (typeof value === 'string') {
          const error = validation.materialType(value);
          if (error) errors[key] = error;
        }
        break;
      case 'deliveryDate':
        if (typeof value === 'string') {
          const error = validation.deliveryDate(value);
          if (error) errors[key] = error;
        }
        break;
    }
  });

  return errors;
}